/**
 * THE SEASON, WEEK BY WEEK — the paper.
 *
 * Race Week has been running from the start and writing about 2,766 events a
 * season into the ledger. Until this screen existed, not one of them was ever
 * shown to anybody: the standings knew who scored, the money screen knew who
 * got paid, and the fact that a man was killed in qualifying at race nine was
 * in the database and on no screen at all.
 *
 * EVERY LINE HERE IS THE ENGINE'S OWN. This component writes no prose. The
 * sentences are `description` strings the rule modules produced, printed with
 * the citation that produced them, exactly as stored. What the screen decides
 * is ORDER and PROMINENCE — see `src/domain/story.ts` — because a week emits
 * seventy-odd events of which sixty are "goes to the bubble stack", and
 * printing them in sequence buries the story under the plumbing.
 *
 * IT ALSO SHOWS THE QUIET. Most weeks have no headline, and the screen says so
 * rather than dressing up a placement as drama. A season where every week is
 * dramatic is a season nobody believes.
 */

import { useMemo, useState } from 'react';
import {
  driverThread,
  seasonHeadlines,
  seasonTone,
  seasonWeeks,
  weigh,
  type StoryEvent,
  type WeekStory,
} from '../domain/story.ts';
import { Cite } from './Cite.tsx';
import ArcStory from './chapters/ArcStory.tsx';
import type { SeasonArc } from '../domain/chapters/index.ts';

export interface SeasonStoryProps {
  events: StoryEvent[];
  /** driverId → name, for the per-driver thread. */
  nameOf?: Map<string, string>;
  seasonNumber?: number;
  /** Race number → the race's name, printed as the week's masthead. */
  raceNames?: Map<number, string>;
  /**
   * D-EZ-110 — the year as a story (`seasonArc`): how it began, the turn, how
   * it ended, the rivalries, the chapter markers. Printed above the ledger's
   * week-by-week — the one part of this page that is narration, and labelled so.
   */
  arc?: SeasonArc | null;
}

export default function SeasonStory({ events, nameOf, seasonNumber, raceNames, arc = null }: SeasonStoryProps) {
  const weeks = useMemo(() => seasonWeeks(events), [events]);
  const headlines = useMemo(() => seasonHeadlines(events), [events]);
  const tone = useMemo(() => seasonTone(events), [events]);

  const [open, setOpen] = useState<number | null>(null);
  const [thread, setThread] = useState<string | null>(null);

  if (weeks.length === 0) {
    return (
      <div className="story">
        {arc && <ArcStory arc={arc} />}
        <p className="dim">
          No race-week events in this season. Race Week writes them as it runs, so a season played before this screen
          existed will still have them — one saved without them never will.
        </p>
      </div>
    );
  }

  return (
    <div className="story">
      {/* ---- D-EZ-110: the year, told — before the ledger's own words ---- */}
      {arc && <ArcStory arc={arc} />}

      {/* ---- the season at a glance ------------------------------------- */}
      <div className="story__tone">
        <Stat n={tone.weeks} l="race weeks" />
        <Stat n={tone.quietWeeks} l="quiet weeks" note="no headline at all" />
        <Stat n={tone.feuds} l="feuds started" hot={tone.feuds > 0} />
        <Stat n={tone.interviews} l="interviews" />
        <Stat n={tone.xEvents} l="ended on X" note="~every race" />
        <Stat n={tone.yEvents} l="ended on Y" note="every 3rd–4th" />
        <Stat n={tone.zEvents} l="ended on Z" hot={tone.zEvents > 0} note="once a season" />
        {tone.suspensions > 0 && <Stat n={tone.suspensions} l="suspensions" hot />}
        {tone.injuries > 0 && <Stat n={tone.injuries} l="injuries" hot />}
        {tone.deaths > 0 && <Stat n={tone.deaths} l="killed" hot />}
      </div>

      {/* ---- the year's biggest moments --------------------------------- */}
      <section className="story__front">
        <h3 className="story__h">The year</h3>
        {headlines.length === 0 ? (
          <p className="dim small">
            Nothing this season rose to a headline — no feuds, no suspensions, nobody hurt. It happens, and it is worth
            seeing said rather than padded out.
          </p>
        ) : (
          <ol className="story__heads">
            {headlines.map((h) => (
              <li key={`${h.raceNumber}-${h.seq}`}>
                <button type="button" className="story__head" onClick={() => setOpen(h.raceNumber)}>
                  <span className="story__wk">Race {h.raceNumber}</span>
                  <span className="story__headtext">{h.description}</span>
                </button>
                <Cite as="cite" className="story__cite">{h.ruleCitation}</Cite>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* ---- week by week ------------------------------------------------ */}
      <section>
        <h3 className="story__h">Week by week</h3>
        <ul className="story__weeks">
          {weeks.map((w) => (
            <Week
              key={w.raceNumber}
              week={w}
              name={raceNames?.get(w.raceNumber)}
              open={open === w.raceNumber}
              onToggle={() => setOpen(open === w.raceNumber ? null : w.raceNumber)}
            />
          ))}
        </ul>
      </section>

      {/* ---- one man's season ------------------------------------------- */}
      {nameOf && nameOf.size > 0 && (
        <section className="story__thread">
          <h3 className="story__h">One driver's season</h3>
          <label className="story__pick">
            <span>Follow</span>
            <select value={thread ?? ''} onChange={(e) => setThread(e.target.value || null)}>
              <option value="">— pick a driver —</option>
              {[...nameOf.entries()]
                .sort((a, b) => a[1].localeCompare(b[1]))
                .map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
            </select>
          </label>
          {thread && <Thread events={events} driverId={thread} name={nameOf.get(thread) ?? thread} />}
        </section>
      )}

      <p className="story__foot">
        {seasonNumber ? `Season ${seasonNumber}. ` : ''}
        Below the story of the year, every sentence on this page was written by the rules engine at the moment it applied
        a rule, and is printed with the page that authorised it. Nothing there is narration. “Ended on X/Y/Z” counts the readings that FINISHED on
        that chart — the charts chain into one another, and a reading that ran TV&nbsp;LIST → X → Y is counted once,
        under Y.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Week({
  week,
  name,
  open,
  onToggle,
}: {
  week: WeekStory;
  name?: string;
  open: boolean;
  onToggle: () => void;
}) {
  const quiet = week.headlines.length === 0;
  return (
    <li className={`story__week${open ? ' is-open' : ''}${quiet ? ' is-quiet' : ''}`}>
      <button type="button" className="story__weekbar" onClick={onToggle} aria-expanded={open}>
        <span className="story__weekno">{week.raceNumber}</span>
        <span className="story__weekname">{name ?? `Race ${week.raceNumber}`}</span>
        <span className="story__weeksum">
          {quiet ? (
            <span className="dim">a quiet week</span>
          ) : (
            <>
              {week.headlines.length} {week.headlines.length === 1 ? 'headline' : 'headlines'}
            </>
          )}
        </span>
        <span className="story__weekn dim">{week.totalEvents}</span>
      </button>

      {open && (
        <div className="story__weekbody">
          {week.headlines.length > 0 && (
            <ul className="story__list story__list--big">
              {week.headlines.map((m) => (
                <li key={m.seq}>
                  <span className="story__dot" style={{ opacity: Math.min(1, weigh(m.momentTag) / 120) }} />
                  <span>{m.description}</span>
                  <Cite as="cite" className="story__cite">{m.ruleCitation}</Cite>
                </li>
              ))}
            </ul>
          )}

          {week.interviews.length > 0 && (
            <div className="story__quotes">
              <h4>In the media centre</h4>
              {week.interviews.map((q) => (
                <blockquote key={q.seq}>
                  {q.description}
                  <Cite as="cite" className="story__cite">{q.ruleCitation}</Cite>
                </blockquote>
              ))}
            </div>
          )}

          <details className="story__more">
            <summary>
              Everything else this week — {week.totalEvents} events in all
            </summary>
            <ul className="story__list story__list--all">
              {week.all.map((m) => (
                <li key={m.seq}>
                  <span className="story__tag">{m.momentTag.replace(/^raceweek\./, '')}</span>
                  <span>{m.description}</span>
                </li>
              ))}
            </ul>
          </details>

          {week.grid && <p className="story__grid">{week.grid.description}</p>}
        </div>
      )}
    </li>
  );
}

function Thread({ events, driverId, name }: { events: StoryEvent[]; driverId: string; name: string }) {
  const weeks = useMemo(() => driverThread(events, driverId), [events, driverId]);
  if (weeks.length === 0) {
    return <p className="dim small">{name} did not appear in a single race-week event all season.</p>;
  }
  const loud = weeks.filter((w) => w.headlines.length > 0).length;
  return (
    <>
      <p className="dim small">
        {name} appears in {weeks.length} of the season's race weeks
        {loud > 0 && `, ${loud} of them eventful`}.
      </p>
      <ul className="story__list">
        {weeks.map((w) =>
          w.moments.map((m) => (
            <li key={`${w.raceNumber}-${m.seq}`}>
              <span className="story__wk">R{w.raceNumber}</span>
              <span>{m.description}</span>
              <Cite as="cite" className="story__cite">{m.ruleCitation}</Cite>
            </li>
          )),
        )}
      </ul>
    </>
  );
}

function Stat({ n, l, note, hot }: { n: number; l: string; note?: string; hot?: boolean }) {
  return (
    <div className={`story__stat${hot ? ' is-hot' : ''}`}>
      <b>{n}</b>
      <span>{l}</span>
      {note && <i>{note}</i>}
    </div>
  );
}
